import { Component, type ErrorInfo, type ReactNode } from 'react';
import { RefreshCw, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface Props {
  children: ReactNode;
  /** Optional label shown in the fallback UI (e.g. "Dashboard", "Report"). */
  pageName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Page-level error boundary for individual routes.
 *
 * Unlike the root ErrorBoundary, this catches errors within a single page
 * while keeping the rest of the app shell (nav, footer, other routes) intact.
 * Users can retry the current page or navigate away without a full app reload.
 */
class PageErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(
      `[PageErrorBoundary${this.props.pageName ? `:${this.props.pageName}` : ''}] Uncaught render error:`,
      error,
      info.componentStack,
    );
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  private handleGoBack = (): void => {
    this.setState({ hasError: false, error: null }, () => {
      window.location.href = '/dashboard';
    });
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const label = this.props.pageName ?? 'This page';

    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="mb-4">
            <span className="text-5xl select-none">⚠</span>
          </div>

          <h2 className="text-xl font-semibold text-white mb-2">
            {label} ran into a problem
          </h2>
          <p className="text-sm text-zinc-400 mb-3">
            An unexpected error occurred while rendering this page.
          </p>

          {this.state.error && (
            <p className="text-xs text-zinc-500 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 mb-5 font-mono break-all text-left">
              {this.state.error.message || 'Unknown error'}
            </p>
          )}

          <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
            <Button
              onClick={this.handleRetry}
              size="sm"
              className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              Retry
            </Button>
            <Button
              onClick={this.handleGoBack}
              variant="outline"
              size="sm"
              className="w-full sm:w-auto bg-zinc-800 border-zinc-700 hover:bg-zinc-700 text-white"
            >
              <ArrowLeft className="w-3.5 h-3.5 mr-1.5" />
              Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default PageErrorBoundary;
